// =====================================================================================
// INVRT Radar - web console - MobileRows (canonical mirror, Story PS1)
//
// Mirror of the INVRT Design System's MobileRows (components/MobileRows/MobileRows.jsx +
// MobileRows.d.ts): the data table's mobile form. A row is a 44px-minimum target: name and
// value on the first line, context beneath, the signal chip last. This retires the three
// hand-rolled card lists the investigation recorded (LeadsSearch, CatalogueSection,
// AccessSection) in favour of one shared component.
//
// Documented deviations from the canonical contract (additive only):
//   - `label` gives the group an accessible name, because a console screen can show
//     several lists at once.
//   - `actions` on a row renders row-level controls in a footer strip OUTSIDE the row's
//     clickable surface, so buttons never nest; when a row carries actions and no
//     onRowClick, the row body renders as a plain container instead of a button.
//   - `className` passes through for page layout.
// =====================================================================================

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Chip, type ChipSignal } from "./chip";

export interface MobileRow {
  id?: string | number;
  name: ReactNode;
  /** Right-aligned mono figure. */
  value?: ReactNode;
  /** Secondary context line: stage, date, owner. */
  meta?: ReactNode;
  signal?: ChipSignal | undefined;
  signalText?: string;
  /** PS1 extension: row-level actions, rendered outside the clickable surface. */
  actions?: ReactNode;
}

export interface MobileRowsProps {
  rows?: MobileRow[];
  onRowClick?: (row: MobileRow) => void;
  /** PS1 extension: accessible name for the group. */
  label?: string;
  className?: string;
}

export function MobileRows({ rows = [], onRowClick, label, className }: MobileRowsProps) {
  return (
    <div
      role={label ? "region" : undefined}
      aria-label={label}
      className={cn("flex flex-col gap-3", className)}
    >
      {rows.map((row, index) => {
        const key = row.id ?? index;
        const interactive = onRowClick !== undefined;
        const body = (
          <>
            <span className="text-sm font-semibold text-ink">{row.name}</span>
            {row.value !== undefined && row.value !== null ? (
              <span className="text-right font-mono text-sm tabular-nums text-ink">{row.value}</span>
            ) : null}
            {row.meta !== undefined && row.meta !== null ? (
              <span className="col-span-full text-xs text-ink-muted">{row.meta}</span>
            ) : null}
            {row.signal ? (
              <span className="col-span-full">
                <Chip signal={row.signal} dot>
                  {row.signalText}
                </Chip>
              </span>
            ) : null}
          </>
        );
        return (
          <div
            key={key}
            className="overflow-hidden rounded-card border border-line bg-surface shadow-card"
          >
            <div
              data-row-name={typeof row.name === "string" ? row.name : undefined}
              {...(interactive
                ? {
                    role: "button" as const,
                    tabIndex: 0,
                    onClick: () => onRowClick(row),
                    onKeyDown: (event: React.KeyboardEvent) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onRowClick(row);
                      }
                    },
                  }
                : {})}
              className={cn(
                "grid min-h-[2.75rem] grid-cols-[1fr_auto] content-start items-baseline gap-x-3 gap-y-1 p-3.5 text-left",
                interactive && "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              )}
            >
              {body}
            </div>
            {row.actions ? (
              <div className="flex flex-wrap gap-2 border-t border-line px-3.5 py-2.5">
                {row.actions}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
